import { Box } from "@mui/material";

interface CountryFlagProps {
    /** ISO 3166-1 alpha-2 country abbreviation (e.g. "US", "CA"). */
    abbreviation: string;
    /** Rendered width in pixels. Height scales to keep flag aspect ratio. */
    width?: number;
}

// The country list uses a few abbreviations that don't match the
// ISO 3166-1 alpha-2 codes flagcdn.com expects. Map them here.
const FLAG_CODE_OVERRIDES: Record<string, string> = {
    UK: "gb",
    // flagcdn has no dedicated flag for these Spanish territories, so fall
    // back to the Spanish flag.
    IC: "es", // Canary Islands
    EA: "es", // Ceuta & Melilla
};

// Abbreviations that have no real flag (e.g. the catch-all "Other" entry).
const NO_FLAG = new Set(["O"]);

/**
 * Renders a small country flag pulled from flagcdn.com, a reputable flag CDN.
 * Returns null when there's no flag for the given abbreviation.
 */
const CountryFlag = ({ abbreviation, width = 20 }: CountryFlagProps) => {
    if (!abbreviation || NO_FLAG.has(abbreviation)) {
        return null;
    }

    const code = (
        FLAG_CODE_OVERRIDES[abbreviation] ?? abbreviation
    ).toLowerCase();

    return (
        <Box
            component="img"
            loading="lazy"
            src={`https://flagcdn.com/w40/${code}.png`}
            srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
            alt=""
            sx={{
                width,
                height: "auto",
                borderRadius: "2px",
                flexShrink: 0,
                objectFit: "contain",
            }}
            onError={(e) => {
                // Hide gracefully if the CDN has no image for this code.
                (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
        />
    );
};

export default CountryFlag;
